import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/mood")({
  component: MoodPage,
});

const EMOTIONS = ["calm", "anxious", "sad", "angry", "hopeful", "tired", "lonely", "grateful", "numb", "overwhelmed"];
const EMOJI = ["😞","😔","😕","😐","🙂","😊","😌","🥰","🤩","🌟"];

function MoodPage() {
  const qc = useQueryClient();
  const [score, setScore] = useState(6);
  const [emotions, setEmotions] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: entries } = useQuery({
    queryKey: ["moods"],
    queryFn: async () => {
      const { data } = await supabase.from("mood_entries").select("*").order("created_at", { ascending: false }).limit(30);
      return data ?? [];
    },
  });

  const toggle = (e: string) => setEmotions((x) => x.includes(e) ? x.filter(y => y !== e) : [...x, e]);

  const save = async () => {
    setSaving(true);
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) { setSaving(false); return; }
    const { error } = await supabase.from("mood_entries").insert({ user_id: u.user.id, score, emotions, note: note || null });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Logged. Be gentle with yourself today.");
    setNote(""); setEmotions([]);
    qc.invalidateQueries({ queryKey: ["moods"] });
    qc.invalidateQueries({ queryKey: ["last-mood"] });
  };

  const avg = entries && entries.length > 0 ? (entries.reduce((s, e) => s + e.score, 0) / entries.length).toFixed(1) : null;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl text-gradient">How are you, really?</h1>
        <p className="text-sm text-muted-foreground mt-1">A 30-second check-in. No right answer.</p>
      </div>

      <div className="glass rounded-3xl p-6 space-y-6">
        <div className="text-center space-y-3">
          <div className="text-6xl">{EMOJI[score - 1]}</div>
          <div className="text-sm text-muted-foreground">Mood: <span className="text-foreground font-medium">{score}/10</span></div>
          <input type="range" min={1} max={10} value={score} onChange={(e) => setScore(+e.target.value)} className="w-full accent-[color:var(--accent)]" />
        </div>

        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">What are you feeling?</div>
          <div className="flex flex-wrap gap-2">
            {EMOTIONS.map((e) => (
              <button key={e} onClick={() => toggle(e)}
                className={`px-3 py-1.5 rounded-full text-xs transition ${emotions.includes(e) ? "bg-accent text-accent-foreground" : "bg-secondary/60 text-muted-foreground hover:text-foreground"}`}>
                {e}
              </button>
            ))}
          </div>
        </div>

        <Textarea placeholder="Anything you'd like to remember about today? (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="bg-secondary/50" />

        <Button onClick={save} disabled={saving} className="w-full glow-primary">{saving ? "Saving…" : "Log this moment"}</Button>
      </div>

      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl">Your past 30 entries</h2>
          {avg && <span className="text-sm text-muted-foreground">Average: {avg}/10</span>}
        </div>
        {entries && entries.length > 0 ? (
          <div className="glass rounded-2xl p-2 space-y-1 max-h-[400px] overflow-y-auto">
            {entries.map((e) => (
              <div key={e.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-secondary/40">
                <div className="text-2xl">{EMOJI[e.score - 1]}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{e.score}/10 {e.emotions.length > 0 && <span className="text-muted-foreground">· {e.emotions.join(", ")}</span>}</div>
                  {e.note && <div className="text-xs text-muted-foreground truncate">{e.note}</div>}
                </div>
                <div className="text-xs text-muted-foreground shrink-0">{format(new Date(e.created_at), "MMM d, h:mm a")}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="glass rounded-2xl p-8 text-center text-sm text-muted-foreground">No entries yet. Your first one is the hardest.</div>
        )}
      </div>
    </div>
  );
}
